import React, { useState } from "react";
import { ChevronDown, Zap, AlertTriangle, TrendingUp } from "lucide-react";

const STAGE_COLORS = {
  "第一幕：铺垫": "var(--color-blue-primary)",
  "第二幕：发展": "var(--color-warning)",
  "第三幕：高潮": "var(--color-error)",
  "第四幕：结局": "var(--color-success)",
};

const IMPORTANCE_STYLE = {
  high: { bg: "color-mix(in srgb, var(--color-error) 10%, transparent)", color: "var(--color-error)", label: "关键" },
  medium: { bg: "color-mix(in srgb, var(--color-warning) 10%, transparent)", color: "var(--color-warning)", label: "重要" },
  low: { bg: "color-mix(in srgb, var(--color-text-muted) 10%, transparent)", color: "var(--color-text-muted)", label: "一般" },
};

const PlotPointsResult = ({ data, theme }) => {
  const [expandedStage, setExpandedStage] = useState(0);
  if (!data) return null;

  const stages = data.stages || [];

  return (
    <div className="space-y-4">
      {/* 总览 */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={16} style={{ color: theme.colors.accentPrimary }} />
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>情节点分析</span>
          {data.totalPoints > 0 && <span className="text-2xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>{data.totalPoints} 个情节点</span>}
        </div>
        {data.summary && <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.summary}</p>}
        {data.paceAnalysis && (
          <div className="mt-3 px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
            <div className="flex items-center gap-1.5 mb-1"><TrendingUp size={12} style={{ color: "var(--color-text-muted)" }} /><span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>节奏分析</span></div>
            <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.paceAnalysis}</p>
          </div>
        )}
      </div>

      {/* 转折点 */}
      {data.turningPoints?.length > 0 && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-2 mb-3"><AlertTriangle size={14} style={{ color: "#F59E0B" }} /><span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>关键转折点</span></div>
          <div className="space-y-2">
            {data.turningPoints.map((tp, i) => (
              <div key={i} className="px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{typeof tp === "string" ? tp : tp.title}</span>
                {tp.significance && <p className="text-2xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>{tp.significance}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 四幕结构 */}
      {stages.map((stage, si) => {
        const color = STAGE_COLORS[stage.name] || theme.colors.accentPrimary;
        const isOpen = expandedStage === si;
        return (
          <div key={si} className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <button onClick={() => setExpandedStage(isOpen ? -1 : si)} className="w-full flex items-center justify-between px-5 py-3 transition-all hover:bg-[var(--color-bg-tertiary)]">
              <div className="flex items-center gap-2.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{stage.name}</span>
                <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{stage.plotPoints?.length || 0} 个情节点</span>
              </div>
              <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "var(--color-text-muted)" }} />
            </button>
            {isOpen && (
              <div className="px-5 pb-4 space-y-2 animate-in fade-in duration-200" style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                {stage.description && <p className="text-xs pt-2" style={{ color: "var(--color-text-muted)" }}>{stage.description}</p>}
                {(stage.plotPoints || []).map((pp, pi) => {
                  const imp = IMPORTANCE_STYLE[pp.importance] || IMPORTANCE_STYLE.medium;
                  return (
                    <div key={pi} className="rounded-lg px-3 py-2.5" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{pp.title}</span>
                        <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: imp.bg, color: imp.color }}>{imp.label}</span>
                        {pp.type && <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{pp.type}</span>}
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{pp.description}</p>
                      {pp.characters?.length > 0 && <div className="flex gap-1 mt-1.5">{pp.characters.map((c, ci) => <span key={ci} className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}15`, color }}>{c}</span>)}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PlotPointsResult;
